import { Fragment, useContext } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { UserMenu, UserPartial } from '@components/user/user-menu'
import { PageHeaderContext } from '@contexts/pageHeader'
import { BREADCRUMBS_BY_PATH, ADMIN_ROUTES, DISABLED_HEADER_ROUTES } from 'routes'
import { ResourceMenu } from './resourceMenu'
import * as css from './styles'

interface Props {
  user?: UserPartial | null
  bgColor: 'light' | 'gray'
}

/**
 * Top bar of application.
 * - Breadcrumbs
 * - Icon based resources menu
 * - User profile menu
 * @param {Props} props React Props
 * @param {Props.user} props.user User details for profile menu
 * @param {Props.bgColor} props.bgColor When top bar is used on a white or gray background.
 * @param {Props.breadcrumbs} props.breadcrumbs An array of breadcrumb navigation.
 * @returns
 */
export const TopBar = ({ user = null, bgColor = 'gray' }: Props) => {
  const location = useLocation()
  const {
    state: { breadcrumbs, variant },
  } = useContext(PageHeaderContext)

  // Set breadcrumbs from a configuration rather than always at the component level.
  // Allows us to get rid of a useEffect just to set the breadcrumb on most pages.
  let pageVariant = variant // Allow override of variant
  const pageBreadcrumbs = breadcrumbs.slice() // default to breadcrumb set by page component.
  if (!breadcrumbs.length) {
    const breadcrumbsByPath = BREADCRUMBS_BY_PATH()
    Object.entries(ADMIN_ROUTES).forEach(([key, path]) => {
      if (matchPath(location.pathname, { path, exact: true })) {
        breadcrumbsByPath[key]?.forEach(p => {
          pageBreadcrumbs.push(p)
          if (p.path?.split('/')?.includes('admin')) pageVariant = 'condensed' // Admin headers are not indented.
        })
      }
    })
  }

  if (DISABLED_HEADER_ROUTES.some(path => matchPath(location.pathname, { path, exact: true }))) {
    return null
  }

  return (
    <css.Header bgColor={bgColor} variant={pageVariant}>
      {pageBreadcrumbs.length > 0 && (
        <css.Breadcrumbs>
          {pageBreadcrumbs.map((bc, index) => (
            <Fragment key={`breadcrumb-${index}`}>
              {bc?.path ? (
                <css.BreadcrumbLink to={bc.path}>{bc.label}</css.BreadcrumbLink>
              ) : (
                <css.BreadcrumbNoLink>{bc.label}</css.BreadcrumbNoLink>
              )}
              {index < pageBreadcrumbs.length - 1 && (
                <css.BreadcrumbDivider>/</css.BreadcrumbDivider>
              )}
            </Fragment>
          ))}
        </css.Breadcrumbs>
      )}
      <css.Icons>
        <ResourceMenu />
      </css.Icons>
      {user && (
        <css.ProfileMenu>
          <UserMenu user={user} />
        </css.ProfileMenu>
      )}
    </css.Header>
  )
}
